
interface SubtitlePart {
    id: number;
    start: number;
    end: number;
    text: string;
}

/**
 * @see https://github.com/MrSlide/parseSRT/blob/master/src/parse-srt.js
 */
export class VideoSubtitle {

    public element: HTMLDivElement;

    public onReady = e5.Signal.create<[string]>();
    public onLoad = e5.Signal.create<[string]>();

    private _subs: SubtitlePart[] = [];
    private _current: SubtitlePart = null;
    private _text: HTMLSpanElement;
    private _loadId: number = 0;
    private _sourceLookup: any = {};
    private _src: string = null;

    private _bind: HTMLVideoElement = null;

    constructor() {
        this.element = document.createElement("div");
        this.element.className = "video-subtitle";
        this.element.innerHTML = `<span class="video-subtitle-text"></span>`;
        this._text = this.element.querySelector(".video-subtitle-text");
    }

    public bind(video: HTMLVideoElement): void {
        this._bind = video;

        this._bind.addEventListener("timeupdate", () => {
            if (this._src) {
                this.update(this._bind.currentTime);
            }
        });
    }

    public update(time: number): void {
        var part = this.getPart(time);

        if (this._current == part) {
            return;
        }

        this._current = part;

        this._text.classList.toggle("hidden", !this._current);

        if (this._current) {
            this._text.innerHTML = this._current.text;
        }
    }

    private getPart(time: number): SubtitlePart {

        if (this._current && this.isInTime(this._current, time)) {
            return this._current;
        }

        for (var part of this._subs) {
            if (this.isInTime(part, time)) {
                return part;
            }
        }

        return null;
    }

    private isInTime(sub: SubtitlePart, time: number): boolean {
        return sub.start <= time && sub.end >= time;
    }

    public async load(src: string): Promise<any> {
        this._subs = [];
        this._current = null;
        this._text.classList.add("hidden");
        this._loadId++;

        if (!src) {
            this._src = null;
            return;
        }

        this._src = src;

        var loadId = this._loadId;
        try {
            var subs: SubtitlePart[] = null;
            if (!this._sourceLookup[src]) {
                var data = await e5.load("text", src);
                subs = this.parse(data);
                this._sourceLookup[src] = subs;
            } else {
                subs = this._sourceLookup[src];
            }

            if (loadId == this._loadId) {
                this._subs = subs;
                if (this._bind) {
                    this.update(this._bind.currentTime);
                }
                this.onReady.dispatch(src);
            }

            this.onLoad.dispatch(src);

        } catch (error) {
            console.warn(error);
        }
        return null;
    }

    private parse(data: string): SubtitlePart[] {
        // declare needed variables and constants
        const subs = []
        const lines = data.split(/(?:\r\n|\r|\n)/gm)
        let endIdx = this.lastNonEmptyLine(lines) + 1
        let idx: number = 0;
        let time: string[];
        let text: string[];
        let sub: SubtitlePart;

        for (let i = 0; i < endIdx; i++) {
            sub = {} as any;
            text = [] as any;

            i = this.nextNonEmptyLine(lines, i)
            sub.id = parseInt(lines[i++], 10)

            // Split on '-->' delimiter, trimming spaces as well
            time = lines[i++].split(/[\t ]*-->[\t ]*/)

            sub.start = this.toSeconds(time[0])

            // So as to trim positioning information from end
            idx = time[1].indexOf(' ')
            if (idx !== -1) {
                time[1] = time[1].substr(0, idx)
            }
            sub.end = this.toSeconds(time[1])

            // Build single line of text from multi-line subtitle in file
            while (i < endIdx && lines[i]) {
                text.push(lines[i++])
            }

            // Join into 1 line, SSA-style linebreaks
            // Strip out other SSA-style tags
            sub.text = text.join('\\N').replace(/\{(\\[\w]+\(?([\w\d]+,?)+\)?)+\}/gi, '')

            // Escape HTML entities
            sub.text = sub.text.replace(/</g, '&lt;').replace(/>/g, '&gt;')

            // Unescape great than and less than when it makes a valid html tag of a supported style (font, b, u, s, i)
            // Modified version of regex from Phil Haack's blog: http://haacked.com/archive/2004/10/25/usingregularexpressionstomatchhtml.aspx
            // Later modified by kev: http://kevin.deldycke.com/2007/03/ultimate-regular-expression-for-html-tag-parsing-with-php/
            sub.text = sub.text.replace(/&lt;(\/?(font|b|u|i|s))((\s+(\w|\w[\w\-]*\w)(\s*=\s*(?:".*?"|'.*?'|[^'">\s]+))?)+\s*|\s*)(\/?)&gt;/gi, '<$1$3$7>');

            sub.text = sub.text.replace(/\\N/gi, '<br/>');
            // sub.text = sub.text.replace(/\\N/gi, ' ');

            //Replace multiply whitespaces with a single one.
            sub.text = sub.text.replace(/\s\s+/g, ' ');


            subs.push(sub)
        }

        return subs
    }

    private toSeconds(time: string) {
        const t = time.split(':')

        try {
            let s = t[2].split(',')

            // Just in case a . is decimal seperator
            if (s.length === 1) {
                s = t[2].split('.')
            }

            return parseFloat(t[0]) * 3600 + parseFloat(t[1]) * 60 + parseFloat(s[0]) + parseFloat(s[1]) / 1000
        } catch (e) {
            return 0
        }
    }

    /**
     * Get the next non empty line number.
     *
     * @private
     * @param {Array<String>} linesArray - All the lines.
     * @param {Number} position - The current line number.
     * @return {Number} - The number of the next non empty line.
     */
    private nextNonEmptyLine(linesArray: string[], position: number) {
        let idx = position

        while (!linesArray[idx]) {
            idx++
        }

        return idx
    }

    private lastNonEmptyLine(linesArray: string[]): number {
        let idx = linesArray.length - 1

        while (idx >= 0 && !linesArray[idx]) {
            idx--
        }

        return idx
    }
}